/**
 * Floto Accessibility Services — a customer's audit request, from intake to
 * delivered report and badge. Lives in the `canvas` database.
 *
 * Lifecycle (only admins move it forward):
 *
 *   submitted → scoping → quoted → paid → delivered
 *                 ↘──────────↘───────↘──→ cancelled
 *
 * - submitted: intake form received; price shown is the customer's estimate.
 * - scoping:   we are confirming pages/flows with the customer.
 * - quoted:    teaser report + Stripe invoice (pay link) sent.
 * - paid:      invoice paid (Stripe) or marked paid (PO / wire). Report due
 *              within REPORT_TURNAROUND_LABEL (upper bound for due dates).
 * - delivered: full report sent, badge issued.
 */

export const SERVICE_REQUEST_STATUSES = [
  'submitted',
  'scoping',
  'quoted',
  'paid',
  'delivered',
  'cancelled',
] as const;
export type ServiceRequestStatus = (typeof SERVICE_REQUEST_STATUSES)[number];

export const AUDIT_STANDARDS = [
  'wcag22-aa',
  'wcag21-aa',
  'wcag22-aaa',
  'en301549-web',
] as const;
export type AuditStandard = (typeof AUDIT_STANDARDS)[number];

/**
 * Web content only. EN 301 549 v3.2.1 clause 9 incorporates WCAG 2.1 A/AA by
 * reference, so an `en301549-web` audit runs as WCAG 2.1 AA and the report
 * cites each finding by clause too (WCAG 1.4.3 → 9.1.4.3). Clauses 10–12
 * (documents, native software, support) are not in scope — the service
 * audits websites and web apps, not native apps.
 */
export const AUDIT_STANDARD_LABELS: Record<AuditStandard, string> = {
  'en301549-web': 'EN 301 549 — web (clause 9)',
  'wcag21-aa': 'WCAG 2.1 AA',
  'wcag22-aa': 'WCAG 2.2 AA',
  'wcag22-aaa': 'WCAG 2.2 AA + AAA advisories',
};

/** Short form for the badge, where space is tight. */
export const AUDIT_STANDARD_BADGE_LABELS: Record<AuditStandard, string> = {
  'en301549-web': 'EN 301 549',
  'wcag21-aa': 'WCAG 2.1 AA',
  'wcag22-aa': 'WCAG 2.2 AA',
  'wcag22-aaa': 'WCAG 2.2 AA',
};

/** EN 301 549 clause for a WCAG success criterion ("1.4.3" → "9.1.4.3"). */
export const en301549Clause = (wcagSc: string): string => `9.${wcagSc}`;

export interface ServiceContact {
  name: string;
  email: string;
  company: string;
  role: string | null;
  phone: string | null;
}

export interface ServiceSite {
  /** Primary URL to audit. */
  url: string;
  /** Other URLs / key flows the customer listed. */
  additionalUrls: string[];
  /** Customer's own page count — what the estimate was priced on. */
  estimatedPages: number;
  standard: AuditStandard;
  /** Parts of the site behind a login. Credentials are never collected in the
   *  form; we arrange access during scoping. */
  requiresLogin: boolean;
  notes: string | null;
  /** ISO date the customer needs it by, if any. */
  deadline: string | null;
}

export interface ServiceDocument {
  id: string;
  name: string;
  contentType: string;
  size: number;
  /** Storage object path. Never sent to the customer. */
  path: string;
  uploadedAt: string;
}

export interface ServiceMoney {
  pages: number;
  ratePerPage: number;
  total: number;
  currency: string;
}

export interface ServiceQuote extends ServiceMoney {
  stripeInvoiceId: string | null;
  /** Stripe-hosted page: pay link and invoice in one. */
  invoiceUrl: string | null;
  invoicePdfUrl: string | null;
  sentAt: string;
}

export interface IssueSummary {
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
}

export interface ServiceFile {
  name: string;
  path: string;
  uploadedAt: string;
}

export interface ServiceTeaser extends ServiceFile {
  summary: IssueSummary;
  /** One or two sentences shown on the status page and in the email. */
  headline: string;
}

export interface ServiceEvent {
  at: string;
  type:
    | 'submitted'
    | 'status'
    | 'quote_sent'
    | 'paid'
    | 'delivered'
    | 'note'
    | 'document';
  /** Admin email, or 'customer' / 'stripe'. */
  by: string;
  note: string | null;
}

/**
 * Where the request came from: the Google Ads click ids and UTM tags on the
 * visitor's landing URL. The click id is what lets a paid audit be uploaded to
 * Google Ads as an offline conversion (`GET /admin/services/conversions`).
 * Every field is null when the landing URL didn't carry it.
 */
export interface ServiceAttribution {
  gclid: string | null;
  /** iOS app-to-web clicks. Not accepted by the click-conversion CSV. */
  gbraid: string | null;
  wbraid: string | null;
  /** When the click id was seen on the landing URL. */
  clickIdCapturedAt: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
}

export interface ServiceRequest {
  id: string;
  /** Short human reference, e.g. FA-7KQ2M. Shown to the customer. */
  reference: string;
  status: ServiceRequestStatus;
  contact: ServiceContact;
  site: ServiceSite;
  documents: ServiceDocument[];
  /** Priced at submission from the customer's own page count. */
  estimate: ServiceMoney;
  quote: ServiceQuote | null;
  teaser: ServiceTeaser | null;
  report: ServiceFile | null;
  badgeId: string | null;
  paidAt: string | null;
  deliveredAt: string | null;
  events: ServiceEvent[];
  /** The intake visitor's anonymous PostHog id — the distinct id the API's
   *  outcome events use. Null when analytics was off; absent on older rows. */
  analyticsId?: string | null;
  /** Null when the landing URL carried no click id or UTM tags; absent on
   *  older rows. */
  attribution?: ServiceAttribution | null;
  createdAt: string;
  updatedAt: string;
}

/** `serviceBadges/{id}` — the public provenance record a badge links to. */
export interface ServiceBadge {
  id: string;
  requestId: string;
  reference: string;
  company: string;
  /** Hostname the badge is valid on. */
  domain: string;
  standard: AuditStandard;
  pagesAudited: number;
  auditedAt: string;
  expiresAt: string;
  revoked: boolean;
}

// ── Responses ──────────────────────────────────────────────────────────────

export interface CreateServiceRequestResult {
  reference: string;
  /** Opaque token for the customer's status page (/r/:token). Only ever
   *  returned here and emailed; stored hashed. */
  statusToken: string;
}

/** What the customer's status page sees — no storage paths, no events by
 *  admin, no Stripe ids. */
export interface PublicServiceRequest {
  reference: string;
  status: ServiceRequestStatus;
  company: string;
  contactName: string;
  url: string;
  standard: AuditStandard;
  estimate: ServiceMoney;
  quote: Omit<ServiceQuote, 'stripeInvoiceId' | 'invoicePdfUrl'> | null;
  teaser: {
    name: string;
    headline: string;
    summary: IssueSummary;
    downloadUrl: string;
  } | null;
  report: { name: string; downloadUrl: string } | null;
  badge: PublicBadge | null;
  documents: { name: string; size: number }[];
  createdAt: string;
  paidAt: string | null;
  deliveredAt: string | null;
}

export interface PublicBadge {
  id: string;
  company: string;
  domain: string;
  standard: AuditStandard;
  pagesAudited: number;
  auditedAt: string;
  expiresAt: string;
  /** valid | expired | revoked — computed at read time. */
  state: 'valid' | 'expired' | 'revoked';
  reference: string;
  verifyUrl: string;
  imageUrl: string;
  /** Ready-to-paste HTML. */
  embedHtml: string;
}

export interface AdminServiceRequestRow {
  id: string;
  reference: string;
  status: ServiceRequestStatus;
  company: string;
  contactEmail: string;
  url: string;
  estimatedPages: number;
  estimateTotal: number;
  quoteTotal: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Paid audits as a Google Ads click-conversion import file. JSON-wrapped so
 * the admin console can fetch it with its bearer token and save it itself.
 */
export interface ServiceConversionExport {
  filename: string;
  csv: string;
  /** Rows in the file. */
  included: number;
  /** Paid in the window but carrying only a gbraid/wbraid, which the CSV
   *  can't take (Google Ads API upload, later). */
  skippedBraidOnly: number;
  /** Paid in the window with no click id at all — not from an ad. */
  skippedNoClickId: number;
}

export interface AdminServiceRequestDetail extends ServiceRequest {
  /** Signed, short-lived download links, keyed by storage path. */
  downloads: Record<string, string>;
  badge: PublicBadge | null;
}
